import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  DatePicker,
  Button,
  Space,
  message,
  Table,
  Divider,
  Tag,
  Row,
  Col,
  Card,
  Radio
} from "antd";
import {
  FileTextOutlined,
  PlusOutlined,
  DeleteOutlined,
  DownloadOutlined,
  DollarCircleOutlined,
  UserOutlined,
  BankOutlined
} from "@ant-design/icons";
import apiClient from "../utils/apiClient";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import dayjs from "dayjs";

const { Option } = Select;
const { TextArea } = Input;

const CATEGORY_OPTIONS = [
  { label: "🏨 Hotel / Accommodation Cost", value: "Hotel Cost", color: "blue" },
  { label: "🚗 Transportation / Vehicle Cost", value: "Transportation Cost", color: "green" },
  { label: "✈️ Flight / Train Tickets", value: "Flight/Train Cost", color: "purple" },
  { label: "⛵ Sightseeing & Activities", value: "Sightseeing & Activities", color: "cyan" },
  { label: "🚩 Guide & Permits", value: "Guide & Permits", color: "orange" },
  { label: "🛡️ Visa & Insurance", value: "Visa & Insurance", color: "magenta" },
  { label: "💼 Service & Convenience Fee", value: "Service Fee", color: "gold" },
  { label: "📌 Other Custom Expense", value: "Other", color: "default" }
];

const AdvancedCustomInvoiceModal = ({ visible, onClose, onSuccess, initialData = null }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customerMode, setCustomerMode] = useState("existing"); // 'existing' | 'custom'

  const [items, setItems] = useState([
    { id: 1, category: "Hotel Cost", description: "Hotel Stay (3 Nights)", quantity: 1, unitPrice: 5000, totalPrice: 5000 },
    { id: 2, category: "Transportation Cost", description: "Private Vehicle Transport", quantity: 1, unitPrice: 3000, totalPrice: 3000 },
  ]);

  const [taxPercent, setTaxPercent] = useState(5); // 5% GST default for travel
  const [discountAmount, setDiscountAmount] = useState(0);

  useEffect(() => {
    if (visible) {
      fetchCustomers();
      if (initialData) {
        populateInitialData(initialData);
      }
    }
  }, [visible, initialData]);

  const fetchCustomers = async () => {
    try {
      const res = await apiClient.get("/customers");
      const list = res.data?.data?.customers || res.data?.data || (Array.isArray(res.data) ? res.data : []);
      setCustomers(list);
    } catch (err) {
      console.error("Fetch customers error:", err);
    }
  };

  const populateInitialData = (data) => {
    if (data.customer?.name || data.customerName) {
      form.setFieldsValue({
        customerName: data.customerName || data.customer?.name,
        customerPhone: data.customerPhone || data.customer?.phone,
        customerEmail: data.customerEmail || data.customer?.email,
        bookingReference: data.bookingReference || data.customer?.leadId,
        invoiceDate: dayjs(),
        dueDate: dayjs().add(7, 'day'),
        notes: "Thank you for booking with us!"
      });
    }

    const prefillItems = [];
    if (data.hotelReservations && data.hotelReservations.length > 0) {
      data.hotelReservations.forEach((h, idx) => {
        const total = (h.pricePerNight || h.price || 0) * (h.nights || 1);
        prefillItems.push({
          id: idx + 1,
          category: "Hotel Cost",
          description: `${h.hotelName || h.name || 'Hotel'} (${h.nights || 1} Nights)`,
          quantity: h.nights || 1,
          unitPrice: h.pricePerNight || h.price || 0,
          totalPrice: total
        });
      });
    }

    if (prefillItems.length > 0) {
      setItems(prefillItems);
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;

    if (field === "quantity" || field === "unitPrice") {
      const q = Number(newItems[index].quantity || 0);
      const p = Number(newItems[index].unitPrice || 0);
      newItems[index].totalPrice = q * p;
    }

    setItems(newItems);
  };

  const addItem = () => {
    setItems([
      ...items,
      { id: Date.now(), category: "Other", description: "", quantity: 1, unitPrice: 0, totalPrice: 0 }
    ]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const subtotal = items.reduce((sum, item) => sum + Number(item.totalPrice || 0), 0);
  const taxAmount = Math.round((subtotal * Number(taxPercent || 0)) / 100);
  const totalAmount = Math.max(0, subtotal + taxAmount - Number(discountAmount || 0));

  const handleSelectCustomer = (customerId) => {
    const cust = customers.find(c => c._id === customerId);
    if (cust) {
      form.setFieldsValue({
        customerName: cust.name,
        customerPhone: cust.phone,
        customerEmail: cust.email,
        customerAddress: cust.address,
        customerGst: cust.gstin || cust.gst
      });
    }
  };

  const handleGenerateInvoice = async (values) => {
    try {
      setLoading(true);

      const invoiceData = {
        bookingReference: values.bookingReference || `INV-${Date.now().toString().slice(-6)}`,
        customerName: values.customerName,
        customerInfo: {
          name: values.customerName,
          phone: values.customerPhone,
          email: values.customerEmail,
          address: values.customerAddress,
          gst: values.customerGst
        },
        issueDate: values.invoiceDate ? values.invoiceDate.toDate() : new Date(),
        dueDate: values.dueDate ? values.dueDate.toDate() : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
        items: items.map(i => ({
          description: `[${i.category}] ${i.description}`,
          quantity: Number(i.quantity || 1),
          unitPrice: Number(i.unitPrice || 0),
          totalPrice: Number(i.totalPrice || 0)
        })),
        subtotal,
        taxAmount,
        discountAmount: Number(discountAmount || 0),
        totalAmount,
        currency: "INR",
        status: values.status || "draft",
        notes: values.notes || "Thank you for choosing Travnok Travel CRM!",
        terms: values.terms || "1. Payment is due within 7 days. 2. Cancellation subject to policy terms."
      };

      // Create invoice record in backend
      const res = await apiClient.post("/invoices", invoiceData);
      const createdInvoice = res.data?.data;

      // Generate & Download PDF
      generatePDFInvoice(values, invoiceData, createdInvoice?.invoiceNumber);

      message.success("Invoice created & PDF downloaded successfully!");
      form.resetFields();
      if (onSuccess) onSuccess(createdInvoice);
      onClose();
    } catch (err) {
      console.error("Failed to generate custom invoice:", err);
      message.error("Failed to generate invoice");
    } finally {
      setLoading(false);
    }
  };

  const generatePDFInvoice = (values, data, invNum) => {
    try {
      const doc = new jsPDF();
      const invoiceNo = invNum || `INV-${Math.floor(100000 + Math.random() * 900000)}`;

      // Header Banner
      doc.setFillColor(79, 70, 229); // #4f46e5 Indigo
      doc.rect(0, 0, 210, 30, 'F');

      doc.setTextColor(255, 255, 255);
      doc.setFontSize(20);
      doc.setFont("helvetica", "bold");
      doc.text("TRAVNOK TRAVEL CRM", 14, 20);

      doc.setFontSize(14);
      doc.text("INVOICE", 196, 20, { align: "right" });

      // Invoice Details Block
      doc.setTextColor(30, 41, 59);
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text(`Invoice No: ${invoiceNo}`, 14, 40);
      doc.text(`Invoice Date: ${dayjs(values.invoiceDate).format("DD MMM YYYY")}`, 14, 46);
      doc.text(`Due Date: ${dayjs(values.dueDate).format("DD MMM YYYY")}`, 14, 52);
      if (values.bookingReference) {
        doc.text(`Ref / Lead ID: ${values.bookingReference}`, 14, 58);
      }

      // Customer Details Box
      doc.setFillColor(248, 250, 252);
      doc.roundedRect(120, 35, 76, 30, 3, 3, 'F');
      doc.setFont("helvetica", "bold");
      doc.text("Billed To:", 125, 42);
      doc.setFont("helvetica", "normal");
      doc.text(`${values.customerName || 'Valued Customer'}`, 125, 48);
      if (values.customerPhone) doc.text(`Phone: ${values.customerPhone}`, 125, 54);
      if (values.customerEmail) doc.text(`Email: ${values.customerEmail}`, 125, 60);

      // Line Items Table
      const tableRows = items.map(item => [
        item.category,
        item.description || 'Service',
        String(item.quantity),
        `INR ${Number(item.unitPrice).toLocaleString()}`,
        `INR ${Number(item.totalPrice).toLocaleString()}`
      ]);

      autoTable(doc, {
        startY: 68,
        head: [["Category", "Description", "Qty", "Unit Cost", "Total Amount"]],
        body: tableRows,
        headStyles: { fillColor: [79, 70, 229], textColor: [255, 255, 255], fontStyle: "bold" },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        styles: { fontSize: 9, cellPadding: 4 }
      });

      const finalY = doc.lastAutoTable.finalY + 10;

      // Summary Totals
      doc.setFont("helvetica", "normal");
      doc.text(`Subtotal:`, 140, finalY);
      doc.text(`INR ${subtotal.toLocaleString()}`, 196, finalY, { align: "right" });

      doc.text(`Tax / GST (${taxPercent}%):`, 140, finalY + 6);
      doc.text(`INR ${taxAmount.toLocaleString()}`, 196, finalY + 6, { align: "right" });

      if (discountAmount > 0) {
        doc.text(`Discount:`, 140, finalY + 12);
        doc.text(`- INR ${Number(discountAmount).toLocaleString()}`, 196, finalY + 12, { align: "right" });
      }

      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.text(`Grand Total:`, 140, finalY + 20);
      doc.text(`INR ${totalAmount.toLocaleString()}`, 196, finalY + 20, { align: "right" });

      // Notes & Terms
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 116, 139);
      doc.text("Notes / Terms:", 14, finalY + 30);
      doc.text(values.notes || "Thank you for your business!", 14, finalY + 36);

      doc.save(`Invoice_${invoiceNo}.pdf`);
    } catch (err) {
      console.error("PDF generation failed:", err);
    }
  };

  const itemColumns = [
    {
      title: "Category",
      dataIndex: "category",
      width: 180,
      render: (_, record, index) => (
        <Select
          value={record.category}
          onChange={(val) => handleItemChange(index, "category", val)}
          style={{ width: "100%" }}
        >
          {CATEGORY_OPTIONS.map((cat) => (
            <Option key={cat.value} value={cat.value}>
              {cat.label}
            </Option>
          ))}
        </Select>
      )
    },
    {
      title: "Custom Field / Item Description",
      dataIndex: "description",
      render: (_, record, index) => (
        <Input
          placeholder="e.g. Hotel Cost, Cab Transfer, Guided Tour"
          value={record.description}
          onChange={(e) => handleItemChange(index, "description", e.target.value)}
        />
      )
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      width: 80,
      render: (_, record, index) => (
        <InputNumber
          min={1}
          value={record.quantity}
          onChange={(val) => handleItemChange(index, "quantity", val)}
          style={{ width: "100%" }}
        />
      )
    },
    {
      title: "Unit Cost (₹)",
      dataIndex: "unitPrice",
      width: 130,
      render: (_, record, index) => (
        <InputNumber
          min={0}
          value={record.unitPrice}
          onChange={(val) => handleItemChange(index, "unitPrice", val)}
          style={{ width: "100%" }}
        />
      )
    },
    {
      title: "Total (₹)",
      dataIndex: "totalPrice",
      width: 120,
      render: (_, record) => (
        <span style={{ fontWeight: 600, color: "#059669" }}>
          ₹{Number(record.totalPrice || 0).toLocaleString()}
        </span>
      )
    },
    {
      title: "",
      width: 50,
      render: (_, __, index) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeItem(index)}
          disabled={items.length === 1}
        />
      )
    }
  ];

  return (
    <Modal
      title={
        <Space>
          <FileTextOutlined style={{ color: "#4f46e5", fontSize: 20 }} />
          <span style={{ fontWeight: 700, fontSize: 18 }}>Generate Custom Customer Invoice</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={920}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleGenerateInvoice}>
        {/* Customer Selection Strategy */}
        <Card size="small" style={{ marginBottom: 16, background: "#f8fafc", borderRadius: 8 }}>
          <Row gutter={16} align="middle">
            <Col span={10}>
              <Form.Item label="Select Customer Source" style={{ marginBottom: 0 }}>
                <Radio.Group
                  value={customerMode}
                  onChange={(e) => setCustomerMode(e.target.value)}
                  optionType="button"
                  buttonStyle="solid"
                >
                  <Radio.Button value="existing">Existing Customer</Radio.Button>
                  <Radio.Button value="custom">New Custom Details</Radio.Button>
                </Radio.Group>
              </Form.Item>
            </Col>

            {customerMode === "existing" && (
              <Col span={14}>
                <Form.Item label="Select Customer" style={{ marginBottom: 0 }}>
                  <Select
                    placeholder="Search and select customer"
                    onChange={handleSelectCustomer}
                    showSearch
                    optionFilterProp="children"
                  >
                    {customers.map((c) => (
                      <Option key={c._id} value={c._id}>
                        {c.name} ({c.phone || c.email || 'No Contact'})
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            )}
          </Row>
        </Card>

        {/* Customer Contact Information Fields */}
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="customerName"
              label="Customer Name"
              rules={[{ required: true, message: "Customer name is required" }]}
            >
              <Input prefix={<UserOutlined />} placeholder="e.g. Rajesh Sharma" />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item name="customerPhone" label="Phone Number">
              <Input placeholder="+91 9876543210" />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item name="customerEmail" label="Email Address">
              <Input placeholder="customer@example.com" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="customerAddress" label="Billing Address">
              <Input placeholder="e.g. 102 Green Park, New Delhi" />
            </Form.Item>
          </Col>

          <Col span={6}>
            <Form.Item name="customerGst" label="GSTIN / Tax ID">
              <Input placeholder="07AAAAA0000A1Z5" />
            </Form.Item>
          </Col>

          <Col span={6}>
            <Form.Item name="bookingReference" label="Lead / Booking Ref">
              <Input placeholder="BK-1002" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="invoiceDate"
              label="Invoice Date"
              initialValue={dayjs()}
              rules={[{ required: true }]}
            >
              <DatePicker style={{ width: "100%" }} format="DD MMM YYYY" />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="dueDate"
              label="Due Date"
              initialValue={dayjs().add(7, 'day')}
              rules={[{ required: true }]}
            >
              <DatePicker style={{ width: "100%" }} format="DD MMM YYYY" />
            </Form.Item>
          </Col>
        </Row>

        <Divider style={{ margin: "12px 0" }}>Custom Cost Line Items</Divider>

        <Table
          columns={itemColumns}
          dataSource={items}
          pagination={false}
          rowKey="id"
          size="small"
          bordered
        />

        <div style={{ marginTop: 12, marginBottom: 16 }}>
          <Button
            type="dashed"
            onClick={addItem}
            icon={<PlusOutlined />}
            style={{ width: "100%", borderColor: "#4f46e5", color: "#4f46e5" }}
          >
            Add Custom Field Line Item (Hotel, Transport, etc.)
          </Button>
        </div>

        {/* Financial Totals Summary Card */}
        <Card size="small" style={{ background: "#f0fdf4", borderColor: "#bbf7d0", marginBottom: 16 }}>
          <Row gutter={16} align="middle">
            <Col span={8}>
              <div>
                <span style={{ fontSize: 13, color: "#475569" }}>Tax / GST Rate (%):</span>
                <InputNumber
                  min={0}
                  max={28}
                  value={taxPercent}
                  onChange={(v) => setTaxPercent(v || 0)}
                  style={{ width: "100%", marginTop: 4 }}
                />
              </div>
            </Col>

            <Col span={8}>
              <div>
                <span style={{ fontSize: 13, color: "#475569" }}>Discount Amount (₹):</span>
                <InputNumber
                  min={0}
                  value={discountAmount}
                  onChange={(v) => setDiscountAmount(v || 0)}
                  style={{ width: "100%", marginTop: 4 }}
                />
              </div>
            </Col>

            <Col span={8} style={{ textAlign: "right" }}>
              <div style={{ fontSize: 12, color: "#64748b" }}>Subtotal: ₹{subtotal.toLocaleString()}</div>
              <div style={{ fontSize: 12, color: "#64748b" }}>GST ({taxPercent}%): ₹{taxAmount.toLocaleString()}</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#059669", marginTop: 4 }}>
                Grand Total: ₹{totalAmount.toLocaleString()}
              </div>
            </Col>
          </Row>
        </Card>

        <Form.Item name="notes" label="Invoice Notes / Terms">
          <TextArea rows={2} placeholder="Terms and payment notes..." defaultValue="Thank you for booking with us!" />
        </Form.Item>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            htmlType="submit"
            icon={<DownloadOutlined />}
            loading={loading}
            style={{ background: "#4f46e5", borderColor: "#4f46e5" }}
          >
            Generate & Download PDF Invoice
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default AdvancedCustomInvoiceModal;
